// src/services/elevenLabsBridge.js
// Lightweight bridge to the ElevenLabs ConvAI web component. It:
// - Lazily injects the widget script
// - Creates an invisible <elevenlabs-convai> element
// - Subscribes to widget DOM events
// - Normalizes events into app-friendly callbacks
// - Maintains an ordered transcript of turns
//
// NOTE: This uses best-effort DOM control for mic start/stop and text send by probing the
// widget's shadow DOM. If ElevenLabs exposes an official JS API in the future,
// replace the mic and text control implementations accordingly.

import { ELEVENLABS_CONFIG } from '../config/intakeConfig';

const WIDGET_TAG = 'elevenlabs-convai';
const SCRIPT_ID = 'elevenlabs-convai-script';

const EVENT_MAP = {
  conversation_started: ['elevenlabs-conversation-started', 'conversation_started'],
  conversation_ended: ['elevenlabs-conversation-ended', 'conversation_ended'],
  user_message: ['elevenlabs-user-message', 'user_message'], // final user turns only
  user_transcript: ['elevenlabs-user-transcript', 'user_transcript'], // streaming partials
  agent_message: ['elevenlabs-agent-message', 'agent_message', 'elevenlabs-agent-response', 'agent_response'],
  widget: ['elevenlabs-widget'] // generic envelope event that carries {type, data}
};

/**
 * @typedef {Object} ConversationTurn
 * @property {'User' | 'AI'} role
 * @property {string} content
 * @property {number} timestamp
 */

/**
 * ElevenLabs Bridge
 */
class ElevenLabsBridge {
  constructor(agentId) {
    this.agentId = agentId;
    this.widgetEl = null;
    this.subscriptions = new Map();
    this.transcript = /** @type {ConversationTurn[]} */([]);
    this.initialized = false;
    this.scriptLoaded = false;

    // de-duplication helpers
    this._dedupe = new Map();
    this._pendingTranscript = '';
    this._pendingTimer = null;

    // External listeners registry
    this.listeners = {
      conversation_started: new Set(),
      conversation_ended: new Set(),
      user_message: new Set(),
      agent_message: new Set(),
      user_transcript: new Set(),
      conversation_completed: new Set() // normalized alias for conversation_ended
    };

    this._onDomEvent = this._onDomEvent.bind(this);
  }

  async init() {
    if (this.initialized) return;
    await this._ensureScript();
    this._mountWidget();
    this._attachDomListeners();
    this.initialized = true;
  }

  destroy() {
    this._detachDomListeners();
    if (this.widgetEl && this.widgetEl.parentNode) {
      this.widgetEl.parentNode.removeChild(this.widgetEl);
    }
    this.widgetEl = null;
    this.initialized = false;

    // reset state
    this.transcript = [];
    if (this._pendingTimer) {
      try { clearTimeout(this._pendingTimer); } catch (_) {}
      this._pendingTimer = null;
    }
    this._pendingTranscript = '';

    // reset listeners
    this.listeners = {
      conversation_started: new Set(),
      conversation_ended: new Set(),
      user_message: new Set(),
      agent_message: new Set(),
      user_transcript: new Set(),
      conversation_completed: new Set()
    };
  }

  on(event, handler) {
    if (!this.listeners[event]) this.listeners[event] = new Set();
    this.listeners[event].add(handler);
    return () => this.off(event, handler);
  }

  off(event, handler) {
    if (this.listeners[event]) {
      this.listeners[event].delete(handler);
    }
  }

  getTranscript() {
    return [...this.transcript];
  }

  clearTranscript() {
    this.transcript = [];
  }

  // Send a user text message to the agent by injecting into the widget (preferred) or by DOM events fallback.
  async sendText(text) {
    if (!text || !text.trim()) return false;

    // Do NOT emit a synthetic user event here. The Chat UI adds the optimistic bubble,
    // and the widget will emit the real user_message which we forward to listeners.
    try {
      const injected = await this._sendTextToWidgetShadow(text);
      if (injected) return true;
    } catch (_) {
      // continue to fallbacks
    }

    // Fallback: dispatch custom events many builds listen for
    try {
      const userEvt = new CustomEvent('elevenlabs-user-message', {
        detail: { type: 'user_message', data: { text } }
      });
      document.dispatchEvent(userEvt);
      try { window.dispatchEvent(userEvt); } catch (_) {}
      try { if (this.widgetEl) this.widgetEl.dispatchEvent(userEvt); } catch (_) {}

      const widgetEvt = new CustomEvent('elevenlabs-widget', {
        detail: { type: 'user_message', data: { text } }
      });
      document.dispatchEvent(widgetEvt);
      try { window.dispatchEvent(widgetEvt); } catch (_) {}
      try { if (this.widgetEl) this.widgetEl.dispatchEvent(widgetEvt); } catch (_) {}
      return true;
    } catch (_) {}

    return false;
  }

  // Attempt to start microphone by clicking the mic button inside the widget's shadow DOM.
  async startVoice() {
    if (!this.widgetEl) return;
    try {
      // First attempt
      const first = this._clickMicButton();
      if (!first) {
        console.warn('ElevenLabsBridge.startVoice: mic button not found');
        return;
      }

      // Wait shortly for the widget to start (listen for conversation_started)
      const started = await this._waitForEventOnce('conversation_started', 800);

      // If not started, try one more click (some embeds need a second click)
      if (!started) {
        console.log('ElevenLabsBridge.startVoice: retrying mic click');
        this._clickMicButton();
      }
    } catch (e) {
      console.warn('ElevenLabsBridge.startVoice: unable to click mic button', e);
    }
  }

  // Attempt to stop microphone similarly.
  async stopVoice() {
    if (!this.widgetEl) return;
    try {
      const root = this.widgetEl.shadowRoot;
      if (!root) return;
      const stopBtn =
        root.querySelector('button[aria-label*="Stop" i]') ||
        root.querySelector('button[aria-label*="Mute" i]') ||
        root.querySelector('[data-testid*="mic" i], [data-test*="mic" i]') ||
        root.querySelector('button');
      if (stopBtn) stopBtn.click();
    } catch (e) {
      console.warn('ElevenLabsBridge.stopVoice: unable to click stop button', e);
    }
  }

  // Internal helper: click mic button if present
  _clickMicButton() {
    try {
      const root = this.widgetEl?.shadowRoot;
      if (!root) return false;
      const micBtn =
        root.querySelector('button[aria-label*="Start" i]') ||
        root.querySelector('button[aria-label*="Speak" i]') ||
        root.querySelector('[data-testid*="mic" i], [data-test*="mic" i]') ||
        root.querySelector('button');
      if (micBtn) {
        micBtn.click();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Internal helper: wait for a single emission of a bridge event with timeout
  _waitForEventOnce(eventName, timeoutMs = 800) {
    return new Promise((resolve) => {
      let resolved = false;
      const off = this.on(eventName, () => {
        if (resolved) return;
        resolved = true;
        off?.();
        resolve(true);
      });
      if (timeoutMs > 0) {
        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          off?.();
          resolve(false);
        }, timeoutMs);
      }
    });
  }

  // Simple dedupe for repeating text within a short window
  _isDuplicateText(roleKey, text) {
    try {
      const key = `${roleKey}:${text}`;
      const now = Date.now();
      const last = this._dedupe.get(key);
      if (last && now - last < 800) return true;
      this._dedupe.set(key, now);
      if (this._dedupe.size > 200) {
        const cutoff = now - 2000;
        for (const [k, ts] of this._dedupe.entries()) {
          if (ts < cutoff) this._dedupe.delete(k);
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  // Finalize pending streaming transcript into a single user turn if no final event arrives
  _finalizePendingTranscript() {
    const text = (this._pendingTranscript || '').trim();
    this._pendingTranscript = '';
    this._pendingTimer = null;
    if (!text) return;
    if (this._isDuplicateText('user', text)) return;
    this._appendTurn({ role: 'User', content: text });
    this._emit('user_message', { text });
  }

  // Ensure the embedded widget shows its text UI and is ready to accept input
  async _ensureChatReady() {
    try {
      const root = this.widgetEl?.shadowRoot;
      if (!root) return false;

      // Heuristics to "open" or focus the chat
      const openBtn =
        Array.from(root.querySelectorAll('button')).find((b) =>
          /(start|open|chat|intake|message|begin)/i.test(b?.textContent || '')
        ) ||
        root.querySelector('button[aria-label*="Start" i]') ||
        root.querySelector('button[aria-label*="Open" i]') ||
        null;

      if (openBtn) {
        try { openBtn.click(); } catch (_) {}
        await new Promise((r) => setTimeout(r, 120));
      }

      // Variant "expanded" should expose a text area; give shadow DOM a moment to render
      let input =
        root.querySelector('textarea') ||
        root.querySelector('input[type="text"], input:not([type])') ||
        null;

      if (!input) {
        // Try a second pass after a short wait
        await new Promise((r) => setTimeout(r, 120));
        input =
          root.querySelector('textarea') ||
          root.querySelector('input[type="text"], input:not([type])') ||
          null;
      }

      return !!input;
    } catch {
      return false;
    }
  }

  // Temporarily unhide widget to improve shadow-DOM interactions (no visual flicker)
  async _withWidgetInteractive(cb) {
    if (!this.widgetEl) return false;
    const el = this.widgetEl;
    const prev = {
      position: el.style.position,
      left: el.style.left,
      right: el.style.right,
      bottom: el.style.bottom,
      width: el.style.width,
      height: el.style.height,
      opacity: el.style.opacity,
      pointer: el.style.pointerEvents,
      z: el.style.zIndex,
      bg: el.style.background
    };
    try {
      // Make it interactive but nearly invisible
      el.style.position = 'fixed';
      el.style.right = '8px';
      el.style.bottom = '8px';
      el.style.width = '360px';
      el.style.height = '240px';
      el.style.left = '';
      el.style.opacity = '0.01';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '2147483647';
      el.style.background = 'transparent';
      await new Promise((r) => setTimeout(r, 50));
      const res = await cb();
      return res;
    } catch {
      return false;
    } finally {
      // Restore original hidden state
      try {
        el.style.position = prev.position;
        el.style.left = prev.left;
        el.style.right = prev.right;
        el.style.bottom = prev.bottom;
        el.style.width = prev.width;
        el.style.height = prev.height;
        el.style.opacity = prev.opacity;
        el.style.pointerEvents = prev.pointer;
        el.style.zIndex = prev.z;
        el.style.background = prev.bg;
      } catch {}
    }
  }

  // Try to inject typed text into the hidden widget's shadow DOM and submit it.
  // Returns true if we believe the widget accepted the text, false otherwise.
  async _sendTextToWidgetShadow(text) {
    try {
      const root = this.widgetEl?.shadowRoot;
      if (!root) return false;

      // Ensure input is present/ready
      const ready = await this._withWidgetInteractive(() => this._ensureChatReady());
      if (!ready) return false;

      // Locate input area
      let input =
        root.querySelector('textarea') ||
        root.querySelector('input[type="text"], input:not([type])') ||
        null;
      if (!input) return false;

      // Focus and set value
      try { input.focus(); } catch (_) {}
      const desc = { bubbles: true, cancelable: true, composed: true };
      input.value = text;
      input.dispatchEvent(new Event('input', desc));
      input.dispatchEvent(new Event('change', desc));

      // Submit via Enter
      await this._withWidgetInteractive(async () => {
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', which: 13, keyCode: 13, bubbles: true, cancelable: true, composed: true }));
        input.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', which: 13, keyCode: 13, bubbles: true, cancelable: true, composed: true }));
      });

      // Try clicking a send/submit control
      const sendBtn =
        root.querySelector('button[aria-label*="Send" i]') ||
        root.querySelector('button[type="submit" i]') ||
        root.querySelector('[data-testid*="send" i], [data-test*="send" i]') ||
        Array.from(root.querySelectorAll('button')).find((b) => /send|submit|enter|reply/i.test(b?.textContent || '')) ||
        null;
      if (sendBtn) {
        await this._withWidgetInteractive(async () => { sendBtn.click(); });
      }

      // If there's a form, try submitting it too
      const form = input.closest('form') || root.querySelector('form');
      if (form) {
        form.dispatchEvent(new Event('submit', desc));
      }

      // Small delay to allow processing
      await new Promise((r) => setTimeout(r, 50));
      return true;
    } catch {
      return false;
    }
  }

  // Internal: ensure the script tag is loaded
  async _ensureScript() {
    if (this.scriptLoaded) return;

    // If the script is already present, mark loaded
    if (document.getElementById(SCRIPT_ID)) {
      this.scriptLoaded = true;
      return;
    }

    const scriptUrl = ELEVENLABS_CONFIG?.WIDGET_SCRIPT_URL || 'https://unpkg.com/@elevenlabs/convai-widget-embed';
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = scriptUrl;
      s.async = true;
      s.type = 'text/javascript';
      s.onload = () => {
        this.scriptLoaded = true;
        resolve(true);
      };
      s.onerror = () => reject(new Error('Failed to load ElevenLabs widget script'));
      document.head.appendChild(s);
    });
  }

  // Internal: create invisible widget element
  _mountWidget() {
    // Avoid duplicates
    const existing = document.querySelector(WIDGET_TAG);
    if (existing) {
      this.widgetEl = existing;
      this._hideWidget(this.widgetEl);
      return;
    }

    const el = document.createElement(WIDGET_TAG);
    // Required agent-id
    el.setAttribute('agent-id', this.agentId || ELEVENLABS_CONFIG?.AGENT_ID || '');
    // Prefer the text-capable layout so programmatic typing works
    try { el.setAttribute('variant', 'expanded'); } catch (_) {}
    // Keep it offscreen/invisible
    this._hideWidget(el);

    // Optionally pass dynamic variables if needed (kept minimal here)
    // el.setAttribute('dynamic-variables', JSON.stringify({ purpose: 'hiring_intake' }));

    document.body.appendChild(el);
    this.widgetEl = el;

    try {
      console.log('🎙️ ElevenLabsBridge: widget mounted', {
        agentId: this.agentId || ELEVENLABS_CONFIG?.AGENT_ID,
        hiddenByDefault: true,
        toggleDebugWith: '?elevenlabsDebug=1 or window.ELEVENLABS_DEBUG=true'
      });
    } catch (_) {}
  }

  _hideWidget(el) {
    // Allow forcing the widget visible for debugging:
    // - URL: ?elevenlabsDebug=1
    // - window.ELEVENLABS_DEBUG = true
    // - localStorage.ELEVENLABS_DEBUG = "true"
    try {
      const qs = new URLSearchParams(window.location.search);
      const dbg = (qs.get('elevenlabsDebug') || '').toLowerCase();
      const fromURL = dbg === '1' || dbg === 'true' || dbg === 'yes' || dbg === 'on';
      const fromWin = typeof window !== 'undefined' && window.ELEVENLABS_DEBUG === true;
      const fromLS = typeof window !== 'undefined' && window.localStorage?.getItem('ELEVENLABS_DEBUG') === 'true';
      const debugVisible = fromURL || fromWin || fromLS;

      if (debugVisible) {
        // Show the widget visibly in the corner for inspection
        el.style.position = 'fixed';
        el.style.right = '16px';
        el.style.bottom = '16px';
        el.style.width = '360px';
        el.style.height = '480px';
        el.style.left = '';
        el.style.top = '';
        el.style.opacity = '1';
        el.style.pointerEvents = 'auto';
        el.style.zIndex = '9999';
        el.style.background = 'white';
        return;
      }
    } catch (_) {
      // ignore and fall back to hidden mode
    }

    // Default: hide the widget (we bridge it to our chat)
    el.style.position = 'fixed';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.left = '-9999px';
    el.style.top = '-9999px';
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';
  }

  _attachDomListeners() {
    // Listen on document + window (+ widget element if available); rely on text-dedupe to avoid duplicates
    const eventNames = Object.values(EVENT_MAP).flat();
    const targets = [
      { key: 'document', target: document },
      { key: 'window', target: window }
    ];
    if (this.widgetEl) {
      targets.push({ key: 'widget', target: this.widgetEl });
    }

    eventNames.forEach((evtName) => {
      const handler = (e) => this._onDomEvent(evtName, e);
      targets.forEach(({ key, target }) => {
        try {
          target.addEventListener(evtName, handler);
          this.subscriptions.set(`${key}:${evtName}`, handler);
        } catch (_) {
          // ignore
        }
      });
    });

    // Also listen for window.postMessage-based events from the widget
    try {
      const msgHandler = (e) => this._onWindowMessage(e);
      window.addEventListener('message', msgHandler);
      this.subscriptions.set('window:message', msgHandler);
    } catch (_) {
      // ignore
    }
  }

  _detachDomListeners() {
    for (const [k, handler] of this.subscriptions.entries()) {
      try {
        const [where, evtName] = k.split(':');
        if (where === 'document') document.removeEventListener(evtName, handler);
        else if (where === 'window') window.removeEventListener(evtName, handler);
        else if (where === 'widget' && this.widgetEl) this.widgetEl.removeEventListener(evtName, handler);
      } catch (_) {
        // ignore
      }
    }
    this.subscriptions.clear();
  }

  // Handle window.postMessage broadcasts from the ElevenLabs widget (some builds use postMessage)
  _onWindowMessage(e) {
    try {
      const payload = e?.data;
      if (!payload || typeof payload !== 'object') return;

      // Normalize into the same shape our _onDomEvent expects
      const type =
        payload.type ||
        payload.event || // some implementations use 'event'
        payload?.detail?.type ||
        '';

      const data =
        payload.data ||
        payload.payload || // alternative field
        payload?.detail?.data ||
        payload;

      if (!type) return;

      // Wrap to mimic a CustomEvent-like object with detail
      const wrapped = { detail: { type, data } };

      // Re-use the DOM event path to fan-in handling
      this._onDomEvent(type, wrapped);
    } catch (_) {
      // ignore
    }
  }

  _onDomEvent(nativeType, event) {
    const detail = event?.detail || {};
    const type = detail?.type || nativeType;
    const data = detail?.data || detail;

    switch (true) {
      case EVENT_MAP.conversation_started.includes(type): {
        this._emit('conversation_started', data);
        break;
      }
      case EVENT_MAP.user_message.includes(type): {
        const text = data?.text || data?.message || '';
        const synthetic = data?.synthetic === true;
        if (text) {
          if (synthetic) break; // skip our own local mirror
          if (this._isDuplicateText('user', text)) break;
          this._appendTurn({ role: 'User', content: text });
          this._emit('user_message', { text });
        }
        break;
      }
      case EVENT_MAP.user_transcript.includes(type): {
        // Streaming partial transcript (voice) - stage and finalize after a short pause.
        const text = data?.text || data?.message || '';
        if (text) {
          this._pendingTranscript = text;
          if (this._pendingTimer) {
            try { clearTimeout(this._pendingTimer); } catch (_) {}
          }
          this._pendingTimer = setTimeout(() => this._finalizePendingTranscript(), 900);
          // Emit as non-bubble signal for typing indicators if needed
          this._emit('user_transcript', { text });
        }
        break;
      }
      case EVENT_MAP.agent_message.includes(type): {
        const text = data?.text || data?.message || '';
        if (text) {
          if (this._isDuplicateText('ai', text)) break;
          this._appendTurn({ role: 'AI', content: text });
          this._emit('agent_message', { text });
        }
        break;
      }
      case EVENT_MAP.conversation_ended.includes(type): {
        this._emit('conversation_ended', data);
        // Also emit normalized completion event with the ordered transcript
        this._emit('conversation_completed', { transcript: this.getTranscript() });
        break;
      }
      default:
        // Unknown/unsupported type - ignore
        break;
    }
  }

  _appendTurn(turn) {
    this.transcript.push({
      role: turn.role === 'User' ? 'User' : 'AI',
      content: `${turn.content}`,
      timestamp: Date.now()
    });
  }

  _emit(event, payload) {
    const set = this.listeners[event];
    if (!set || set.size === 0) return;
    set.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error(`elevenLabsBridge listener error for ${event}:`, e);
      }
    });
  }
}

/**
 * Factory to create/init a bridge instance
 */
export async function createElevenLabsBridge(agentId) {
  const bridge = new ElevenLabsBridge(agentId);
  await bridge.init();
  return bridge;
}
